export interface EmbedFixResult {
  value: string
  fixed: boolean
}

/**
 * src patterns the old editor's video/audio dialogs actually emit.
 */
const VIDEO_SRC_PATTERN = /youtube\.com\/embed\/|player\.vimeo\.com\/video\//i
const AUDIO_SRC_PATTERN = /w\.soundcloud\.com\/player/i
const IFRAME_TAG_PATTERN = /<iframe\b[^>]*>/gi

/**
 * Classifies a legacy iframe tag by its src, using the same domains the old
 * editor's video/audio dialogs generate.
 */
export function detectEmbedType(iframeTag: string): 'video' | 'audio' | null {
  if (VIDEO_SRC_PATTERN.test(iframeTag)) return 'video'
  if (AUDIO_SRC_PATTERN.test(iframeTag)) return 'audio'
  return null
}

export function fixEmbedMarkup(html: string): EmbedFixResult {
  if (!html || typeof html !== 'string' || !html.includes('<iframe')) {
    return { value: html, fixed: false }
  }

  let fixed = false
  const value = html.replace(IFRAME_TAG_PATTERN, (tag) => {
    if (/\bdata-type\s*=/i.test(tag)) return tag

    const type = detectEmbedType(tag)
    if (!type) return tag

    fixed = true
    const urlMatch = tag.match(/\burl\s*=\s*"([^"]*)"/i)
    const dataUrlAttr = urlMatch ? ` data-url="${urlMatch[1]}"` : ''
    return tag.replace(/^<iframe\b/i, `<iframe data-type="${type}"${dataUrlAttr}`)
  })

  return { value, fixed }
}

export function fixLegacyPassageMarkup(config: any): any {
  if (!config?.models || !Array.isArray(config.models)) return config

  let anyModelChanged = false

  const updatedModels = config.models.map((model: any) => {
    if (!Array.isArray(model?.passages)) return model

    let anyPassageChanged = false

    const updatedPassages = model.passages.map((passage: any) => {
      if (typeof passage?.text !== 'string') return passage

      const { value, fixed } = fixEmbedMarkup(passage.text)
      if (!fixed) return passage

      anyPassageChanged = true
      return { ...passage, text: value }
    })

    if (!anyPassageChanged) return model

    anyModelChanged = true
    return { ...model, passages: updatedPassages }
  })

  return anyModelChanged ? { ...config, models: updatedModels } : config
}
